import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { VagaListagem } from "@/types/vaga";
import type { VagaStatus } from "@/utils/vaga-status";

export type VagaDateField = "cadastro" | "aprovacao" | "reprovacao";
export type VagaListSort = "created_desc" | "created_asc";

/** Filtros da visão em lista de /vagas. `""`/`null`/vazio = filtro inativo. */
export interface VagaListFilters {
  search: string;
  agenciaId: string;
  statuses: VagaStatus[];
  freelancerId: string | null;
  dateField: VagaDateField | null;
  dateFrom: string | null; // YYYY-MM-DD
  dateTo: string | null; // YYYY-MM-DD
  sort: VagaListSort;
}

export const DEFAULT_VAGA_LIST_FILTERS: VagaListFilters = {
  search: "",
  agenciaId: "",
  statuses: [],
  freelancerId: null,
  dateField: null,
  dateFrom: null,
  dateTo: null,
  sort: "created_desc",
};

/**
 * Argumentos comuns às RPCs get_vagas_list_page / get_vagas_list_count. A data só
 * é aplicada quando há um campo escolhido E ao menos um limite — evita mandar
 * dateField sem intervalo.
 */
function toRpcArgs(filters: VagaListFilters) {
  const hasDate =
    !!filters.dateField && (!!filters.dateFrom || !!filters.dateTo);
  return {
    p_search: filters.search.trim() || null,
    p_agencia_id: filters.agenciaId || null,
    p_statuses: filters.statuses.length ? filters.statuses : null,
    p_freelancer_id: filters.freelancerId || null,
    p_date_field: hasDate ? filters.dateField : null,
    p_date_from: hasDate ? filters.dateFrom : null,
    p_date_to: hasDate ? filters.dateTo : null,
  };
}

/** Total FILTRADO — muda só com os filtros, não com page/sort/pageSize. */
export function useVagasListCount(filters: VagaListFilters) {
  const args = toRpcArgs(filters);
  return useQuery({
    queryKey: ["vagas-list-count", args],
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async (): Promise<number> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("get_vagas_list_count", args);
      if (error) throw error;
      return (data as number) ?? 0;
    },
  });
}

/**
 * Busca TODAS as linhas que batem nos filtros atuais (não paginado) para export.
 * Reusa o RPC da página com um limite alto; o backend já rankeia+hidrata só o
 * conjunto pedido. `max` é um teto de segurança.
 */
export async function fetchAllVagasForExport(
  filters: VagaListFilters,
  max = 5000,
): Promise<VagaListagem[]> {
  const args = toRpcArgs(filters);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data, error } = await (supabase as any).rpc("get_vagas_list_page", {
    ...args,
    p_sort: filters.sort,
    p_limit: max,
    p_offset: 0,
  });
  if (error) throw error;
  return (data ?? []) as unknown as VagaListagem[];
}

/** Uma página da lista de vagas (hidratada, mesmo shape do kanban). */
export function useVagasListPage(
  filters: VagaListFilters,
  page: number,
  pageSize: number,
) {
  const args = toRpcArgs(filters);
  return useQuery({
    queryKey: ["vagas-list-page", args, filters.sort, page, pageSize],
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async (): Promise<VagaListagem[]> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any).rpc("get_vagas_list_page", {
        ...args,
        p_sort: filters.sort,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
      });
      if (error) throw error;
      return (data ?? []) as unknown as VagaListagem[];
    },
  });
}
